import type { Cycle, Page, Project } from "@baton/api";
import { sidebarBranchesKey, sidebarBranchHeightsKey } from "@baton/core";
import { messageKey, useI18n } from "@baton/i18n";
import { Archive, CircleDashed, CircleDot, FolderKanban, Layers, Repeat } from "@baton/icons";
import { ErrorState } from "@baton/ui";
import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { problemOf } from "../../app/problems.ts";
import { cyclesQuery, meQuery, projectsQuery } from "../../app/queries.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { useGeneralProject, withGeneral } from "../../project/general/general.ts";
import type { ModeDestination, ModeSidebarProps } from "../modes/modes.tsx";
import { ClientHeadings } from "./ClientHeadings.tsx";
import { clientHeadings } from "./clients.ts";
import { isWorkspaceMember } from "../state/permissions.ts";
import { GlideList } from "./GlideList.tsx";
import { EntriesPlaceholder } from "./placeholders.tsx";
import { SIDEBAR_PILL, SidebarItem, SidebarSection } from "./Sidebar.tsx";

/**
 * FR-015-002, `contracts/screens.md` § Sidebar § Work (2026-10-19, by the owner's decision after
 * feedback that the product read as a software-development tool, its sprints sitting in Home while
 * Clients had a mode of its own): the Work mode's tree, the Team's tasks, sprints and projects, which
 * Home's Team section carried until that day. One section: All tasks, Backlog (its own row since
 * 2026-10-02, outside the sprint rows), Active sprint, Sprints and Projects. Active sprint opens the
 * running sprint in one click and is current on that sprint's page; Sprints opens the list, and is
 * current there and on any other sprint's page; Projects opens the projects index, which lists every
 * project and carries New project. Sprints and Projects are branching rows (2026-09-17, by the owner's
 * request): a disclosure at the row's end opens the ten most recent sprints, newest start first, or
 * the first ten projects under it, each a row to that sprint or project, then the count of the rest,
 * which opens the list; which branches stand open is kept per workspace. A sprint is the team's, not
 * one project's, so the sprint rows are here rather than nested under each project. The Projects
 * branch lists the Team's General project first (2026-10-03, `withGeneral`), above any client
 * heading. While a visible project is on a client the Projects branch lists every project under its
 * client's heading instead (020 FR-020-173, `ClientHeadings.tsx`). A branch's loading placeholder is
 * as tall as the branch last drew its entries in this workspace, so a reload with a branch open moves
 * no row under it (the owner's meeting of 2026-09-30).
 */
interface WorkRow extends ModeDestination {
  readonly icon: ReactNode;
  /** Names the row current, for a row whose destination redirects elsewhere or shares a prefix with another; the router decides when absent. */
  readonly current?: (pathname: string, ws: string, activeCycleId: string | null) => boolean;
}

/** How many entries a branch shows before the count of the rest. */
const BRANCH_LIMIT = 10;

const WORK_ROWS: readonly WorkRow[] = [
  { id: "all-items", label: messageKey("nav.allItems"), path: "all-tasks", icon: <Layers /> },
  // The Team's backlog, a row of its own beside the sprint rows and never under the Sprints branch, at `/{ws}/backlog` (2026-10-02, by the owner's decision: "Remove backlog from the actual sprint section itself").
  { id: "backlog", label: messageKey("nav.backlog"), path: "backlog", icon: <CircleDashed /> },
  { id: "cycle-active", label: messageKey("nav.cycleActive"), path: "sprints/active", icon: <CircleDot />, current: (pathname, ws, active) => active !== null && pathname === `/${ws}/sprints/${active}` },
  { id: "cycles", label: messageKey("nav.cycles"), path: "sprints", icon: <Repeat />, current: (pathname, ws, active) => (pathname === `/${ws}/sprints` || pathname.startsWith(`/${ws}/sprints/`)) && (active === null || pathname !== `/${ws}/sprints/${active}`) },
  { id: "projects", label: messageKey("nav.projects"), path: "projects", icon: <FolderKanban />, current: (pathname, ws) => pathname === `/${ws}/projects` || pathname.startsWith(`/${ws}/projects/`) },
  // The Team's archive (2026-11-03, 001 FR-186): what an import holds until it is picked into a sprint; a guest reads none of it.
  { id: "archive", label: messageKey("nav.archive"), path: "archive", icon: <Archive />, memberOnly: true },
];

/** Every destination the Work sidebar lists, named as its screen is. */
export const WORK_DESTINATIONS: readonly ModeDestination[] = WORK_ROWS;

/** The sprints newest start first, so the branch's ten are the most recent. */
function recentCycles(cycles: readonly Cycle[]): readonly Cycle[] {
  return [...cycles].sort((a, b) => b.start_date.localeCompare(a.start_date));
}

interface BranchEntry {
  readonly id: string;
  readonly to: string;
  readonly label: string;
  readonly current: boolean;
}

/** What a branch draws from: one read, or the projects with General pinned first, which waits on two. */
interface BranchRead<T> {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: readonly T[] | undefined;
  readonly refetch: () => unknown;
}

interface BranchEntriesProps<T> {
  readonly query: BranchRead<T>;
  readonly entryOf: (entry: T) => BranchEntry;
  readonly empty: string;
  readonly listTo: string;
  readonly onNavigate?: () => void;
  /** Another drawing of the whole list, in place of the first rows and the count of the rest: the projects under their client headings; null keeps the flat rows. */
  readonly grouped?: (rows: readonly T[]) => ReactNode | null;
  /** How tall the entries last drew, which the placeholder holds; undefined before they ever have. */
  readonly reserved: number | undefined;
  /** Told each height the drawn entries take, for the next load's placeholder. */
  readonly onHeight: (height: number) => void;
}

/** The drawn entries of a branch, reporting each height they take while the branch stands open (a closed branch is hidden and measures 0, which is not reported). */
function MeasuredEntries({ onHeight, children }: { readonly onHeight: (height: number) => void; readonly children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const report = useRef(onHeight);
  useLayoutEffect(() => {
    report.current = onHeight;
  });
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const measure = () => {
      const height = Math.round(element.getBoundingClientRect().height);
      if (height > 0) report.current(height);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);
  return <div ref={ref}>{children}</div>;
}

/** A branch's entries: the placeholder, the error, the empty line, or the rows and the count of the rest. */
function BranchEntries<T>({ query, reserved, onHeight, ...props }: BranchEntriesProps<T>) {
  if (query.isPending) return <EntriesPlaceholder height={reserved} />;
  return (
    <MeasuredEntries onHeight={onHeight}>
      <BranchContent query={query} {...props} />
    </MeasuredEntries>
  );
}

function BranchContent<T>({ query, entryOf, empty, listTo, onNavigate, grouped }: Omit<BranchEntriesProps<T>, "reserved" | "onHeight">) {
  const { t } = useI18n();
  if (query.isPending) return null;
  if (query.isError) return <ErrorState form="inline" problem={problemOf(query.error)} onRetry={() => void query.refetch()} />;
  const rows = query.data ?? [];
  if (rows.length === 0) return <p className="b-sidebar__empty">{empty}</p>;
  const groups = grouped?.(rows) ?? null;
  if (groups !== null) return groups;
  const shown = rows.slice(0, BRANCH_LIMIT).map(entryOf);
  const rest = rows.length - shown.length;
  const navigateProps = onNavigate === undefined ? {} : { onNavigate };
  return (
    <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL}>
      {shown.map((entry) => (
        <SidebarItem key={entry.id} to={entry.to} label={entry.label} current={entry.current} nested {...navigateProps} />
      ))}
      {rest > 0 && <SidebarItem to={listTo} label={t("nav.branch.more", { count: rest })} current={false} nested muted {...navigateProps} />}
    </GlideList>
  );
}

export function WorkSidebar({ ws, onNavigate }: ModeSidebarProps) {
  const { t, locale } = useI18n();
  const runtime = useRuntime();
  const { pathname } = useLocation();
  // The sprints read the shell already makes, so the Active sprint row knows which page is its own and the Sprints branch has its entries.
  const cycles = useQuery({ ...cyclesQuery(runtime, ws), select: recentCycles });
  const projectList = useQuery({ ...projectsQuery(runtime, ws), select: (page: Page<Project>) => page.items });
  // General pinned first (2026-10-03, `withGeneral`); the branch waits for both reads, so General never pushes the rows down after they drew.
  const general = useGeneralProject(runtime, ws);
  const projects: BranchRead<Project> = {
    isPending: projectList.isPending || general.pending,
    isError: projectList.isError,
    error: projectList.error,
    data: projectList.data === undefined ? undefined : withGeneral(projectList.data, general.project),
    refetch: projectList.refetch,
  };
  const activeCycleId = cycles.data?.find((cycle) => cycle.status === "active")?.id ?? null;
  const [branches, setBranches] = useState<Record<string, boolean>>(() => runtime.storage.get(sidebarBranchesKey, ws) ?? {});
  const branchOf = (id: string, label: string, children: ReactNode) => {
    const expanded = branches[id] ?? false;
    return {
      expanded,
      label: expanded ? t("nav.branch.hide", { label }) : t("nav.branch.show", { label }),
      onExpandedChange: (next: boolean) => {
        setBranches((current) => {
          const updated = { ...current, [id]: next };
          runtime.storage.set(sidebarBranchesKey, updated, ws);
          return updated;
        });
      },
      children,
    };
  };
  // Read once: the heights of the last drawing, which this load's placeholders hold; each new drawing is written for the next load.
  const [reservedHeights] = useState<Record<string, number>>(() => runtime.storage.get(sidebarBranchHeightsKey, ws) ?? {});
  const heightOf = (id: string) => ({
    reserved: reservedHeights[id],
    onHeight: (height: number) => {
      const stored = runtime.storage.get(sidebarBranchHeightsKey, ws) ?? {};
      if (stored[id] !== height) runtime.storage.set(sidebarBranchHeightsKey, { ...stored, [id]: height }, ws);
    },
  });
  const navigateProps = onNavigate === undefined ? {} : { onNavigate };
  const me = useQuery(meQuery(runtime));
  const member = isWorkspaceMember(me.data, ws);
  const branchesOf = {
    cycles: branchOf("cycles", t("nav.cycles").toLocaleLowerCase(), <BranchEntries query={cycles} entryOf={(cycle) => ({ id: cycle.id, to: `/${ws}/sprints/${cycle.id}`, label: cycle.name, current: pathname === `/${ws}/sprints/${cycle.id}` })} empty={t("nav.cycles.empty")} listTo={`/${ws}/sprints`} {...heightOf("cycles")} {...navigateProps} />),
    projects: branchOf(
      "projects",
      t("nav.projects").toLocaleLowerCase(),
      <BranchEntries
        query={projects}
        entryOf={(project) => {
          const base = `/${ws}/projects/${encodeURIComponent(project.identifier)}`;
          return { id: project.id, to: `${base}/tasks`, label: project.name, current: pathname === base || pathname.startsWith(`${base}/`) };
        }}
        empty={t("nav.projects.empty")}
        listTo={`/${ws}/projects`}
        {...heightOf("projects")}
        grouped={(rows) => {
          // General stands first, above the client headings, being no client's and every project's fallback.
          const pinned = rows.find((project) => project.id === general.id);
          const headings = clientHeadings(rows.filter((project) => project.id !== general.id), locale);
          if (headings.length === 0) return null;
          const base = pinned === undefined ? null : `/${ws}/projects/${encodeURIComponent(pinned.identifier)}`;
          return (
            <>
              {pinned !== undefined && base !== null && (
                <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL}>
                  <SidebarItem to={`${base}/tasks`} label={pinned.name} current={pathname === base || pathname.startsWith(`${base}/`)} nested {...navigateProps} />
                </GlideList>
              )}
              <ClientHeadings ws={ws} headings={headings} pathname={pathname} {...navigateProps} />
            </>
          );
        }}
        {...navigateProps}
      />,
    ),
  };
  const row = (entry: WorkRow) => (
    <SidebarItem
      key={entry.id}
      to={`/${ws}/${entry.path}`}
      label={t(entry.label)}
      icon={entry.icon}
      {...(entry.current === undefined ? {} : { current: entry.current(pathname, ws, activeCycleId) })}
      {...(entry.id === "cycles" ? { branch: branchesOf.cycles } : entry.id === "projects" ? { branch: branchesOf.projects } : {})}
      {...navigateProps}
    />
  );
  return (
    <SidebarSection id="work-overview" title={t("nav.work.section.overview")}>
      <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL} data-sidebar-destinations>
        {WORK_ROWS.filter((entry) => entry.memberOnly !== true || member).map(row)}
      </GlideList>
    </SidebarSection>
  );
}
